import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { LogOut, Loader2, AlertCircle, ChevronRight, BookOpen, Key, ShieldX, ClipboardCheck, ChevronDown } from 'lucide-react';
import { useUserContext, usePermissions } from '../context/UserContext';
import { Capability } from '@mapp-byte-connect/config';
import { AUDITOR_FEEDBACK_ENABLED } from '../config/featureFlags';

function Logo() {
  return (
    <Link to="/forms" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
      <img src="/logo.svg" alt="Byte Pulse" className="w-6 h-7" />
      <span className="font-display text-base font-bold tracking-tight text-ink hidden sm:block">Byte Pulse</span>
    </Link>
  );
}

function Breadcrumbs() {
  const location = useLocation();
  const path = location.pathname;

  // Don't show breadcrumbs on the forms list (home)
  if (path === '/forms' || path === '/') return null;

  if (path === '/api-guide') {
    return (
      <nav className="flex items-center gap-1 text-xs text-ink-tertiary">
        <Link to="/forms" className="hover:text-ink transition-colors">Forms</Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-ink-secondary font-medium">API Guide</span>
      </nav>
    );
  }

  if (path === '/api-keys') {
    return (
      <nav className="flex items-center gap-1 text-xs text-ink-tertiary">
        <Link to="/forms" className="hover:text-ink transition-colors">Forms</Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-ink-secondary font-medium">API Keys</span>
      </nav>
    );
  }

  if (path.startsWith('/auditor-feedback/')) {
    const section = path.includes('/respondent-errors')
      ? 'Respondent Errors'
      : path.includes('/monitoring')
        ? 'Monitoring'
        : 'Triggers';
    return (
      <nav className="flex items-center gap-1 text-xs text-ink-tertiary">
        <Link to="/forms" className="hover:text-ink transition-colors">Forms</Link>
        <ChevronRight className="w-3 h-3" />
        <Link to="/auditor-feedback/triggers" className="hover:text-ink transition-colors">Auditor Feedback</Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-ink-secondary font-medium">{section}</span>
      </nav>
    );
  }

  const crumbs: { label: string; to?: string }[] = [
    { label: 'Pulses', to: '/forms' },
  ];

  if (path.includes('/forms/new')) {
    crumbs.push({ label: 'New Form' });
  } else if (path.match(/\/forms\/[^/]+\/edit/)) {
    crumbs.push({ label: 'Edit' });
  } else if (path.match(/\/forms\/[^/]+\/submit/)) {
    crumbs.push({ label: 'Submit' });
  } else if (path.match(/\/forms\/[^/]+\/analytics/)) {
    crumbs.push({ label: 'Analytics' });
  } else if (path.match(/\/forms\/[^/]+\/translations\/[^/]+$/)) {
    crumbs.push({ label: 'Translation' });
  } else if (path.match(/\/forms\/[^/]+\/submissions/)) {
    crumbs.push({ label: 'Submissions' });
  } else if (path.match(/\/forms\/[^/]+$/)) {
    crumbs.push({ label: 'Details' });
  }

  return (
    <nav className="flex items-center gap-1 text-xs text-ink-tertiary">
      {crumbs.map((crumb, i) => (
        <span key={i} className="flex items-center gap-1">
          {i > 0 && <ChevronRight className="w-3 h-3" />}
          {crumb.to ? (
            <Link to={crumb.to} className="hover:text-ink transition-colors">{crumb.label}</Link>
          ) : (
            <span className="text-ink-secondary font-medium">{crumb.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

function LoginScreen({ onLogin }: { onLogin: () => void }) {
  return (
    <div className="min-h-screen bg-surface flex">
      {/* Left decorative panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-ink items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
        <div className="relative z-10 max-w-md px-12">
          <Logo />
          <h2 className="font-display text-4xl font-bold text-ink-inverse mt-8 leading-tight">
            Capture signals.<br />Drive action.
          </h2>
          <p className="text-stone-400 mt-4 text-base leading-relaxed">
            Capture frontline signals, generate operational insights, and trigger action across the superapp ecosystem.
          </p>
        </div>
      </div>

      {/* Right login panel */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-10">
            <Logo />
          </div>
          <h1 className="font-display text-2xl font-bold text-ink">Welcome back</h1>
          <p className="text-ink-secondary text-sm mt-1.5 mb-8">Sign in to your account to continue.</p>
          <button onClick={onLogin} className="btn-primary w-full py-3">
            Sign In
          </button>
        </div>
      </div>
    </div>
  );
}

function AuthErrorScreen({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <div className="min-h-screen bg-surface flex items-center justify-center p-6">
      <div className="w-full max-w-sm text-center">
        <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-5">
          <AlertCircle className="w-6 h-6 text-red-500" />
        </div>
        <h1 className="font-display text-xl font-bold text-ink">Authentication failed</h1>
        <p className="text-ink-secondary text-sm mt-2 mb-6">{error}</p>
        <button onClick={onRetry} className="btn-primary w-full">Try Again</button>
      </div>
    </div>
  );
}

function UserProfileMenu({
  user,
  onLogout,
}: {
  user: { firstName: string; lastName: string; email: string; yumId: string };
  onLogout: () => void;
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-2 rounded-lg px-1.5 py-1 hover:bg-surface-sunken transition-colors"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <div className="w-7 h-7 rounded-full bg-ink flex items-center justify-center">
          <span className="text-2xs font-bold text-ink-inverse">
            {user.firstName[0]}{user.lastName[0]}
          </span>
        </div>
        <span className="hidden sm:block text-sm font-medium text-ink">
          {user.firstName}
        </span>
        <ChevronDown className={`hidden sm:block w-3.5 h-3.5 text-ink-tertiary transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1.5 w-56 rounded-xl border border-ring bg-surface-raised shadow-xl py-1 z-50"
        >
          <div className="px-3 py-2 border-b border-ring/60">
            <p className="text-sm font-semibold text-ink">{user.firstName} {user.lastName}</p>
            <p className="text-xs text-ink-tertiary truncate">{user.email}</p>
            <p className="text-2xs text-ink-tertiary mt-0.5">{user.yumId}</p>
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-ink-secondary hover:bg-surface-sunken hover:text-ink transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export default function AppLayout({ children }: { children?: ReactNode }) {
  const { isLoading, user, authError, logout, initAuth, retryAuth, brandId, environment } =
    useUserContext();
  const { can } = usePermissions();
  const location = useLocation();
  const isEditorRoute = location.pathname.endsWith('/edit') || location.pathname === '/forms/new';
  // When used as a wrapper (e.g. the /forms/:formId dispatcher) render the provided
  // children; when used as a route element render the nested <Outlet />.
  const content = children ?? <Outlet />;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-ink-tertiary animate-spin" />
      </div>
    );
  }

  if (authError) return <AuthErrorScreen error={authError} onRetry={retryAuth} />;
  if (!user) return <LoginScreen onLogin={initAuth} />;

  // Users without form management capabilities cannot access the admin UI
  if (!can(Capability.FORM_LIST)) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-6">
        <div className="w-full max-w-sm text-center">
          <div className="w-12 h-12 rounded-2xl bg-surface-sunken flex items-center justify-center mx-auto mb-5">
            <ShieldX className="w-6 h-6 text-ink-tertiary" />
          </div>
          <h1 className="font-display text-xl font-bold text-ink">No access</h1>
          <p className="text-ink-secondary text-sm mt-2 mb-6">
            Your account does not have access to form management. You can only access forms via direct submission links.
          </p>
          <button onClick={logout} className="btn-secondary">Sign Out</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface flex flex-col">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-surface-overlay backdrop-blur-lg border-b border-ring/50">
        <div className="max-w-screen-xl mx-auto flex items-center justify-between h-14 px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <Logo />
            <Breadcrumbs />
          </div>

          <div className="flex items-center gap-3">
            {/* Auditor Feedback — hidden unless the feature flag is on, then gated by AUDITOR_FEEDBACK_READ */}
            {AUDITOR_FEEDBACK_ENABLED && can(Capability.AUDITOR_FEEDBACK_READ) && (
              <Link
                to="/auditor-feedback/triggers"
                className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-ink-tertiary hover:text-ink hover:bg-surface-sunken transition-colors"
              >
                <ClipboardCheck className="w-3.5 h-3.5" />
                Auditor Feedback
              </Link>
            )}

            {/* API Guide link — visible to admins/devs */}
            <Link
              to="/api-guide"
              className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-ink-tertiary hover:text-ink hover:bg-surface-sunken transition-colors"
            >
              <BookOpen className="w-3.5 h-3.5" />
              API
            </Link>

            {/* API Keys link */}
            <Link
              to="/api-keys"
              className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-ink-tertiary hover:text-ink hover:bg-surface-sunken transition-colors"
            >
              <Key className="w-3.5 h-3.5" />
              Keys
            </Link>

            {/* Environment / Brand */}
            <div className="hidden sm:flex items-center gap-1.5">
              {environment && environment !== 'production' && (
                <span className="badge bg-surface-sunken text-ink-secondary border border-ring">
                  {environment}
                </span>
              )}
              {brandId && (
                <span className="badge bg-surface-sunken text-ink-secondary border border-ring">
                  {brandId}
                </span>
              )}
            </div>

            <UserProfileMenu user={user} onLogout={logout} />
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {isEditorRoute ? (
          // Editor pages get full-width, no padding — they manage their own layout
          content
        ) : (
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
              {content}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
